import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";

import { Dialog } from "primereact/dialog";
import { TaskInputGroup } from "api/model/airflow-task/TaskInputGroup";
import { T } from "services/lang/LanguageService";
import { PermissionGuard } from "components/wrappers/PermissionGuard";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import React, { useState } from "react";
import JSONEditor from "components/basic/json-editor/JSONEditor";
import { JSONEditorMode } from "utils/enums";
import { DatasetStringValueMapperDTO } from "../../../charts/interfaces";
import { Tooltip } from "primereact/tooltip";

export enum MapperEditorMode {
  CREATE = "CREATE",
  EDIT = "EDIT",
}

interface CreateEditDatasetMapperProps {
  mapperData?: DatasetStringValueMapperDTO;
  mode: MapperEditorMode;
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  onSave: (data: DatasetStringValueMapperDTO) => void;
}

const CreateEditDatasetMapper = ({
  mapperData,
  mode,
  isVisible,
  setIsVisible,
  onSave,
}: CreateEditDatasetMapperProps) => {
  const initialMappings = mapperData
    ? Object.entries(mapperData.mappings).map(([key, value]) => ({
        key,
        value: String(value),
        icon: mapperData.mappingIcons?.[key] ?? "",
      }))
    : [{ key: "", value: "", icon: "" }];

  const [mappingsList, setMappingsList] =
    useState<{ key: string; value: string; icon: string }[]>(initialMappings);

  const [isJSONEditorVisible, setIsJSONEditorVisible] = useState<boolean>(false);

  const [currentMapper, setCurrentMapper] = useState<DatasetStringValueMapperDTO>({
    id: mapperData?.id,
    title: mapperData?.title ?? "",
    global: mapperData?.global ?? false,
    company: mapperData?.company,
    mappings: Object.fromEntries(initialMappings.map(m => [m.key, m.value])),
    mappingIcons: Object.fromEntries(initialMappings.map(m => [m.key, m.icon ?? ""])),
  });

  const handleMappingChange = (
    index: number,
    field: "key" | "value" | "icon",
    newValue: string
  ) => {
    const updated = [...mappingsList];
    updated[index] = { ...updated[index], [field]: newValue };
    setMappingsList(updated);

    const mappings: Record<string, string> = {};
    const mappingIcons: Record<string, string> = {};

    updated.forEach(({ key, value, icon }) => {
      if (key) {
        mappings[key] = value;
        if (icon) mappingIcons[key] = icon;
      }
    });

    setCurrentMapper(prev => ({
      ...prev,
      mappings,
      mappingIcons,
    }));
  };

  const addMapping = () => {
    setMappingsList(prev => [...prev, { key: "", value: "", icon: "" }]);
  };

  const removeMapping = (index: number) => {
    const updatedMappings = mappingsList.filter((_, i) => i !== index);
    setMappingsList(updatedMappings);

    const mappings: Record<string, string> = {};
    const mappingIcons: Record<string, string> = {};

    updatedMappings.forEach(({ key, value, icon }) => {
      if (key) {
        mappings[key] = value;
        if (icon) mappingIcons[key] = icon;
      }
    });

    setCurrentMapper(prev => ({
      ...prev,
      mappings,
      mappingIcons,
    }));
  };

  const handleSave = () => {
    if (currentMapper.title.trim() === "") {
      easasAlert({
        summary: T("alert.title-missing"),
        detail: T("alert.please-enter-a-title"),
        severity: "error",
      });
      return;
    }

    if (Object.keys(currentMapper.mappings).length === 0) {
      easasAlert({
        summary: T("alert.mappings-missing"),
        detail: T("alert.please-enter-at-least-one-mapping"),
        severity: "error",
      });
      return;
    }

    onSave(currentMapper);
  };

  const handleJSONEditorChange = (
    e: DatasetStringValueMapperDTO | Record<string, TaskInputGroup[]>
  ) => {
    if ("title" in e && "mappings" in e) {
      const updatedMapper = e as DatasetStringValueMapperDTO;

      const updatedList = Object.entries(updatedMapper.mappings).map(([key, value]) => ({
        key,
        value: String(value),
        icon: updatedMapper.mappingIcons?.[key] ?? "",
      }));

      setCurrentMapper(updatedMapper);
      setMappingsList(updatedList);
    } else {
      easasAlert({
        summary: T("alert.json-is-not-valid"),
        detail: T("easas-alert.invalid-mapper-format"),
        severity: "error",
      });
    }
  };

  const footerTemplate = (
    <div className="flex flex-row justify-content-between">
      <PermissionGuard permissions="HIDDEN_FEATURE_ACCESS">
        <Button
          label={T("word.json-editor")}
          icon="pi pi-code"
          className="component-editor-button icon-blue"
          onClick={() => setIsJSONEditorVisible(true)}
        />
      </PermissionGuard>
      <div className="flex flex-row gap-2">
        <Button
          className="component-editor-button"
          icon="pi pi-times"
          label={T("word.cancel")}
          onClick={() => setIsVisible(false)}
        />
        <Button
          className="component-editor-button icon-green"
          icon="pi pi-save"
          label={mode === MapperEditorMode.CREATE ? T("action.create") : T("action.save")}
          onClick={handleSave}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      style={{ width: "min(100vw, 500px)", maxHeight: "600px" }}
      header={T("action.edit-mapper")}
      visible={isVisible}
      onHide={() => setIsVisible(false)}
      footer={footerTemplate}
      resizable
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-row justify-content-between">
          <p className="w-6 mb-0 align-self-center">{T("label.mapper.dataset-mapper-title")}</p>
          <InputText
            className="w-full"
            value={currentMapper.title}
            onChange={e => setCurrentMapper(prev => ({ ...prev, title: e.target.value }))}
            placeholder={T("label.mapper.new-dataset-mapper-title")}
          />
        </div>
        <PermissionGuard permissions="ADMIN">
          <div className="flex col-12 align-items-center">
            <p>{T("word.global")}</p>
            <InputSwitch
              checked={currentMapper.global}
              onChange={e => setCurrentMapper(prev => ({ ...prev, global: e.value }))}
            />
          </div>
        </PermissionGuard>
        <div className="flex flex-row justify-content-between">
          <div className="font-bold text-xl">{T("word.mappings")}</div>
          <Button
            icon="pi pi-file"
            label={T("label.icons-list")}
            className="component-editor-button"
            onClick={() => window.open("https://primereact.org/tooltip/", "_blank")}
          />
        </div>
        {mappingsList.map((mapping, index) => (
          <div key={index} className="flex w-12 align-items-center gap-2">
            <InputText
              className="w-full"
              value={mapping.key}
              onChange={e => handleMappingChange(index, "key", e.target.value)}
              placeholder={T("word.key")}
            />

            <InputText
              className="w-full"
              value={mapping.value}
              onChange={e => handleMappingChange(index, "value", e.target.value)}
              placeholder={T("word.value")}
            />

            <InputText
              className="w-full"
              value={mapping.icon}
              onChange={e => handleMappingChange(index, "icon", e.target.value)}
              placeholder={T("word.icon")}
              tooltip="ex: pi pi-home"
              tooltipOptions={{ position: "bottom" }}
            />

            <Button
              type="button"
              icon="pi pi-trash"
              className="const-del-btn  p-button-rounded p-button-text p-button-danger"
              onClick={() => removeMapping(index)}
            />
          </div>
        ))}
        <div className="w-full">
          <Button
            className="component-editor-button icon-green"
            icon="pi pi-plus"
            label={T("action.add-mapping")}
            onClick={addMapping}
          />
        </div>
      </div>
      {isJSONEditorVisible && (
        <JSONEditor
          config={currentMapper}
          isVisible={isJSONEditorVisible}
          setIsVisible={setIsJSONEditorVisible}
          mode={JSONEditorMode.MAPPER}
          onMapperChange={handleJSONEditorChange}
        />
      )}
    </Dialog>
  );
};

export default CreateEditDatasetMapper;
